import { useContext } from 'react'
import { useMutation, useQuery } from 'react-query'
import { apiGet, apiPost, apiDelete, apiPatch } from 'api'
import AuthContext from 'context/auth-context'
import { baseUrl } from 'api/helpers'
import axios from 'axios'

const getReceiptScans = async (token, params) => {
  const url = 'receipt_scans'
  const data = await apiGet({ url, token, params })
  return data
}

export const useReceiptScans = ({ params, options }) => {
  const token = useContext(AuthContext)

  const queryFn = () => getReceiptScans(token, params)
  const queryKey = ['receipt-scans', params]

  return useQuery(queryKey, queryFn, {
    ...options
  })
}

const getReceiptScan = async (token, id) => {
  const url = `receipt_scans/${id}`
  const data = await apiGet({ url, token })
  return data
}

export const useReceiptScan = ({ id, options }) => {
  const token = useContext(AuthContext)

  const queryFn = () => getReceiptScan(token, id)
  const queryKey = ['receipt-scan', id]

  return useQuery(queryKey, queryFn, {
    enabled: !!id,
    ...options
  })
}

const uploadReceipt = async (token, file, scanType = 'receipt') => {
  const url = `${baseUrl}/receipt_scans`
  const formData = new FormData()
  formData.append('receipt_scan[receipt_image]', file)
  formData.append('receipt_scan[scan_type]', scanType)

  const response = await axios.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${token}`
    }
  })

  return response.data
}

export const useUploadReceipt = ({ scanType = 'receipt', options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    (file) => uploadReceipt(token, file, scanType),
    { ...options }
  )
}

const assignReceipt = async (token, receiptScanId, transactionDetailParams) => {
  const url = `${baseUrl}/receipt_scans/${receiptScanId}/assign`

  const response = await axios.patch(
    url,
    transactionDetailParams,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    }
  )
  return response.data
}

export const useAssignReceipt = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    ({ receiptScanId, pcTransactionDetailId, apTransactionDetailId }) => {
      const params = apTransactionDetailId
        ? { ap_transaction_detail_id: apTransactionDetailId }
        : { pc_transaction_detail_id: pcTransactionDetailId }
      return assignReceipt(token, receiptScanId, params)
    },
    { ...options }
  )
}

const updateReceiptScan = async (token, id, data) => {
  const url = `receipt_scans/${id}`
  const response = await apiPatch({ url, token, objectName: 'receipt_scan', params: data })
  return response
}

export const useUpdateReceiptScan = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    ({ id, data }) => updateReceiptScan(token, id, data),
    { ...options }
  )
}

const deleteReceiptScan = async (token, id) => {
  const url = `receipt_scans/${id}`
  const response = await apiDelete({ url, token })
  return response
}

export const useDeleteReceiptScan = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    (id) => deleteReceiptScan(token, id),
    { ...options }
  )
}

const unmatchReceiptScan = async (token, id) => {
  const url = `${baseUrl}/receipt_scans/${id}/unmatch`
  const response = await axios.post(url, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useUnmatchReceiptScan = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    (id) => unmatchReceiptScan(token, id),
    { ...options }
  )
}

const retryReceiptScan = async (token, id) => {
  const url = `${baseUrl}/receipt_scans/${id}/retry`
  const response = await axios.post(url, {}, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useRetryReceiptScan = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    (id) => retryReceiptScan(token, id),
    { ...options }
  )
}

const bulkDeleteReceiptScans = async (token, ids) => {
  const url = `${baseUrl}/receipt_scans/bulk_destroy`
  const response = await axios.delete(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    data: { ids }
  })
  return response.data
}

export const useBulkDeleteReceiptScans = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    (ids) => bulkDeleteReceiptScans(token, ids),
    { ...options }
  )
}

const bulkRetryReceiptScans = async (token, ids) => {
  const url = `${baseUrl}/receipt_scans/bulk_retry`
  const response = await axios.post(url, { ids }, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return response.data
}

export const useBulkRetryReceiptScans = ({ options }) => {
  const token = useContext(AuthContext)

  return useMutation(
    (ids) => bulkRetryReceiptScans(token, ids),
    { ...options }
  )
}

// Fetch match candidates for a receipt scan with pagination and filters
const getReceiptMatches = async (token, receiptScanId, params = {}) => {
  const { limit = 10, offset = 0, filters = {} } = params

  const searchParams = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString()
  })

  // Add filter parameters
  if (filters.exact_amount) searchParams.append('exact_amount', 'true')
  if (filters.exact_date) searchParams.append('exact_date', 'true')
  if (filters.exact_vendor) searchParams.append('exact_vendor', 'true')
  if (filters.exact_card) searchParams.append('exact_card', 'true')
  if (filters.exact_invoice) searchParams.append('exact_invoice', 'true')
  if (filters.exact_po) searchParams.append('exact_po', 'true')

  const url = `${baseUrl}/receipt_scans/${receiptScanId}/matches?${searchParams.toString()}`

  const response = await axios.get(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })

  return response.data
}

export const useReceiptMatches = ({ receiptScanId, limit = 10, offset = 0, filters = {}, options = {} }) => {
  const token = useContext(AuthContext)

  const queryFn = () => getReceiptMatches(token, receiptScanId, { limit, offset, filters })
  const queryKey = ['receipt-matches', receiptScanId, limit, offset, filters]

  return useQuery(queryKey, queryFn, {
    enabled: !!receiptScanId,
    ...options
  })
}

// Fetch more matches (for load more functionality)
export const fetchMoreMatches = async (token, receiptScanId, limit, offset) => {
  return getReceiptMatches(token, receiptScanId, { limit, offset })
}
